<script setup>
import { ref } from 'vue';
import { RouterLink } from 'vue-router';
import router from '@/router';
import { API } from '@/services/index';
import { CREATE_PROJECT_MUTATION } from '@/services/project/mutations/project/index.js';
import { GET_WORKSPACES_QUERY } from '@/services/workspace/queries/workspace/index.js';
import { useMutation } from '@tanstack/vue-query';
import { useSetupStore } from '@/stores/setup.store.js';

const formRules = {
  projectName: (value) => !!value || 'Project name is required'
};

const newAccount = computed(() => useSetupStore().newAccount);
const newProjectForm = ref();
const isFormValid = ref(false);
const projectName = ref(null);
const dueDate = ref(null);
const isCreator = ref(null);
const creatorCount = ref(null);
const projectIcon = ref(null);
const progress = ref(95);
const isLoading = ref(false);
const createProjectErrors = ref(null);

const onSubmitHandler = async () => {
  try {
    isLoading.value = true;
    const workspace = await API.sendQuery('/graphql/workspace', GET_WORKSPACES_QUERY, { account_id: Number(newAccount.value.id) });
    if (workspace.data.data.workspaces.length > 0) {
      const payload = {
        workspace_id: Number(workspace.data.data.workspaces[0].id),
        name: projectName.value,
        creator_count: Number(creatorCount.value),
        due_date: dueDate.value
      };

      await createProjectMutation.mutateAsync(payload);
      isLoading.value = false;
    }
  } catch (error) {
    console.log(error);
    createProjectErrors.value = error.message;
  }
};

const createProjectMutation = useMutation({
  mutationFn: async (payload) => {
    return await API.sendQuery('/graphql/project', CREATE_PROJECT_MUTATION, payload);
  },
  onSuccess: (data) => {
    if (data.data.data.createProject) {
      router.push(`/project/${data.data.data.createProject.id}`); // redirect to project page;
    }

    if (data.data.errors) {
      createProjectErrors.value = data.data.errors;
      isLoading.value = false;
    }
  },
  onError: (error) => {
    console.log('error', error);
    isLoading.value = false;
    createProjectErrors.value = 'System error. Please try again later.';
  }
});

// const formRules = {
//   name: (value) => !!value || 'Name is required',
//   dueDate: (value) => !!value || 'Due date is required'
// }

// onBeforeMount(async () => {
//   if (setup.getProject) {
//     await router.push('/onboard/invite-team')
//   } else {
//     // name.value = setup.getAccountType.name
//   }
// })
</script>

<template>
  <div>
    <div class="tw-text-center">
      <h3 class="tw-font-mono tw-text-lg tw-font-bold tw-text-primary-blue">Create Your First Project</h3>
      <div class="tw-text-[0.813rem]">Start your first project! Fill in the details below and start collaborating!</div>
    </div>
    <v-form ref="newProjectForm" v-model="isFormValid">
      <div class="tw-mt-10">
        <div class="tw-mb-2 tw-flex tw-w-full tw-flex-col tw-space-y-5">
          <div class="tw-flex tw-w-full tw-flex-col">
            <v-text-field
              v-model="projectName"
              :rules="[formRules.projectName]"
              variant="outlined"
              color="primary-blue"
              placeholder="Project Name"
              hide-details="auto"
              density="comfortable"
              :disabled="isLoading"
            ></v-text-field>
          </div>
          <div class="tw-mb-2 tw-flex tw-w-full tw-flex-col">
            <v-text-field
              v-model="dueDate"
              type="date"
              variant="outlined"
              color="primary-blue"
              placeholder="Due Date"
              hide-details="auto"
              density="comfortable"
              :disabled="isLoading"
            ></v-text-field>
          </div>
        </div>
      </div>
      <div class="tw-mt-20 tw-w-full">
        <v-btn
          color="primary-blue"
          size="large"
          rounded="lg"
          block
          flat
          class="tw-font-bold tw-capitalize"
          style="letter-spacing: 0px"
          :loading="isLoading"
          :disabled="isLoading || !isFormValid"
          @click="onSubmitHandler"
        >
          Continue
        </v-btn>
        <div class="tw-mt-3 tw-flex tw-justify-between">
          <RouterLink class="tw-cursor-pointer tw-text-[0.75rem] tw-font-bold tw-text-[#9B9B9B]" to="/onboard/invite-team" tw-relative="">
            Back
          </RouterLink>
          <RouterLink class="tw-cursor-pointer tw-text-[0.75rem] tw-font-bold tw-text-primary-blue" :disabled="isLoading" to="/"> Skip </RouterLink>
        </div>
        <div class="tw-my-5">
          <v-progress-linear class="tw-mt-4" color="primary-blue" model-value="95"></v-progress-linear>
        </div>
      </div>
    </v-form>
  </div>
</template>

<style scoped></style>
